import type { Prize } from '../../shared/types';
import { q, qo } from '../../shared/dom';
import { t } from '../../shared/i18n';
import { getState } from '../../shared/store/store';
import { eventPrizes } from '../../shared/store/selectors';
import { currentEvent } from '../../shared/session/session';
import { toast } from '../../shared/ui/toast';
import { Snd } from './audio';
import { live } from './live';
import { updateConsole } from './console';
import { ctx2d } from './fx/canvas';
import { act, startDraw } from './studio';

/**
 * A Roda da Fortuna em canvas. A roda É a marca: o nome da comunidade vai gravado
 * no aro e gira com ela. Construção por anéis, de fora para dentro:
 *   halo → lâmpadas de marquise → aro gravado → bisel → setores → ícone + nome
 *   → medalhão central → brilho de vidro → ponteiro
 * Tudo em devicePixelRatio: não pixela numa live 1080p nem em 4K.
 */
const SEG_DARK = '#0D1018', SEG_HOT = '#FF6A00', SEG_GOLD = '#C9861E';
const RIM_TEXT = 'RUEDA DE LA FORTUNA ✦ ';

let wheelAngle = 0;
let wheelPrizes: Prize[] = [];
let wheelWin = -1;
let wheelSpinning = false;
let wheelRAF: number | null = null;
let rimCache: { radius: number; items: { ch: string; a: number }[] } | null = null;

const segColor = (i: number, n: number): string => (n % 2 === 1 && i === n - 1) ? SEG_GOLD : (i % 2 ? SEG_HOT : SEG_DARK);
const inkFor = (col: string): string => col === SEG_DARK ? '#F4F1E7' : '#1E0E02';

function shade(hex: string, p: number): string {
  const n = parseInt(hex.slice(1), 16);
  const f = (v: number): number => Math.max(0, Math.min(255, Math.round(v + (p / 100) * (p < 0 ? v : 255 - v))));
  return `rgb(${f(n >> 16 & 255)},${f(n >> 8 & 255)},${f(n & 255)})`;
}

/** Reparte o nome da comunidade por todo o aro, repetido e justificado. */
function buildRim(x: CanvasRenderingContext2D, radius: number, size: number): { ch: string; a: number }[] {
  if (rimCache && rimCache.radius === radius) return rimCache.items;
  x.save();
  x.font = `700 ${size}px Archivo, "Helvetica Neue", Arial, sans-serif`;
  const chars = [...RIM_TEXT];
  const unit  = chars.reduce((s, ch) => s + x.measureText(ch).width, 0);
  const reps  = Math.max(2, Math.round((Math.PI * 2 * radius) / unit));
  const slack = (Math.PI * 2 * radius - unit * reps) / (chars.length * reps);
  const items: { ch: string; a: number }[] = [];
  let cursor = 0;
  for (let r = 0; r < reps; r++) {
    for (const ch of chars) {
      const w = x.measureText(ch).width + slack;
      items.push({ ch, a: (cursor + w / 2) / radius });
      cursor += w;
    }
  }
  x.restore();
  rimCache = { radius, items };
  return items;
}

export function drawWheel(ts = 0): void {
  const c = qo<HTMLCanvasElement>('#wheel'); if (!c) return;
  const x = ctx2d(c);
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const css = c.clientWidth || 560;
  const W = Math.max(320, Math.round(css * dpr));
  if (c.width !== W) { c.width = W; c.height = W; rimCache = null; }
  const R = W / 2;

  x.setTransform(1, 0, 0, 1, 0, 0);
  x.clearRect(0, 0, W, W);

  if (!wheelPrizes.length) {
    const evw = currentEvent();
    wheelPrizes = (evw ? eventPrizes(evw.id) : getState().prizes).filter(p => p.status === 'pending').slice(0, 10);
  }
  const n = wheelPrizes.length;
  if (!n) return;

  x.translate(R, R);
  const TAU = Math.PI * 2;

  /* halo */
  const halo = x.createRadialGradient(0, 0, R * .6, 0, 0, R);
  halo.addColorStop(0, 'rgba(255,154,61,.28)');
  halo.addColorStop(1, 'rgba(255,154,61,0)');
  x.fillStyle = halo; x.beginPath(); x.arc(0, 0, R, 0, TAU); x.fill();

  /* lâmpadas de marquise */
  const bulbs = Math.min(48, Math.max(30, n * 5));
  const bulbR = R * .972, br = R * .0145;
  const chase = wheelSpinning ? Math.floor(ts / 55) : Math.floor(ts / 430);
  for (let i = 0; i < bulbs; i++) {
    const a = (i / bulbs) * TAU - Math.PI / 2;
    const lit = wheelSpinning ? ((i + chase) % 3 === 0) : ((i + chase) % 2 === 0);
    const bx = Math.cos(a) * bulbR, by = Math.sin(a) * bulbR;
    const g = x.createRadialGradient(bx, by, 0, bx, by, br * 3.2);
    g.addColorStop(0,  lit ? 'rgba(255,250,228,.98)' : 'rgba(255,214,120,.32)');
    g.addColorStop(.3, lit ? 'rgba(255,205,88,.72)'  : 'rgba(150,110,40,.2)');
    g.addColorStop(1,  'rgba(255,180,60,0)');
    x.fillStyle = g; x.beginPath(); x.arc(bx, by, br * 3.2, 0, TAU); x.fill();
    x.fillStyle = lit ? '#FFFAE4' : '#7C5F21';
    x.beginPath(); x.arc(bx, by, br, 0, TAU); x.fill();
  }

  /* aro metálico com o nome gravado */
  const rimOut = R * .944, rimIn = R * .846;
  let rim: CanvasGradient;
  if (x.createConicGradient) {
    rim = x.createConicGradient(wheelAngle, 0, 0);
    ([[0, '#6E4C10'], [.07, '#E4BA5E'], [.14, '#FFF6D8'], [.24, '#B8842A'], [.37, '#7A5512'],
      [.5, '#FFEEBB'], [.62, '#A97A24'], [.74, '#664710'], [.86, '#FFF3CC'], [1, '#6E4C10']] as [number, string][])
      .forEach(s => rim.addColorStop(s[0], s[1]));
  } else {
    rim = x.createLinearGradient(-R, -R, R, R);
    rim.addColorStop(0, '#FFEEBB'); rim.addColorStop(.5, '#7A5512'); rim.addColorStop(1, '#FFF6D8');
  }
  x.beginPath(); x.arc(0, 0, rimOut, 0, TAU); x.arc(0, 0, rimIn, 0, TAU, true);
  x.fillStyle = rim; x.fill();
  x.lineWidth = Math.max(1, R * .005); x.strokeStyle = 'rgba(0,0,0,.6)';
  x.beginPath(); x.arc(0, 0, rimOut, 0, TAU); x.stroke();
  x.beginPath(); x.arc(0, 0, rimIn, 0, TAU); x.stroke();

  const rimMid = (rimOut + rimIn) / 2, rimSize = Math.round(R * .044);
  const items = buildRim(x, rimMid, rimSize);
  x.save();
  x.rotate(wheelAngle - Math.PI / 2);
  x.font = `700 ${rimSize}px Archivo, "Helvetica Neue", Arial, sans-serif`;
  x.textAlign = 'center'; x.textBaseline = 'middle';
  items.forEach(it => {
    x.save();
    x.translate(Math.cos(it.a) * rimMid, Math.sin(it.a) * rimMid);
    x.rotate(it.a + Math.PI / 2);
    x.fillStyle = 'rgba(255,244,214,.45)'; x.fillText(it.ch, 0, R * .004);   // luz do gravado
    x.fillStyle = '#4A3408';               x.fillText(it.ch, 0, 0);          // sulco do gravado
    x.restore();
  });
  x.restore();

  /* setores */
  const segOut = rimIn - R * .016, segIn = R * .15;
  x.save(); x.rotate(wheelAngle);
  for (let i = 0; i < n; i++) {
    const prize = wheelPrizes[i] as Prize;
    const a0 = (i / n) * TAU - Math.PI / 2, a1 = ((i + 1) / n) * TAU - Math.PI / 2, mid = (a0 + a1) / 2;
    const col = segColor(i, n);

    x.beginPath(); x.moveTo(0, 0); x.arc(0, 0, segOut, a0, a1); x.closePath();
    const g = x.createRadialGradient(0, 0, segIn, 0, 0, segOut);
    g.addColorStop(0, shade(col, -40)); g.addColorStop(.58, col); g.addColorStop(1, shade(col, 16));
    x.fillStyle = g; x.fill();

    if (!wheelSpinning && i === wheelWin) {                      // o setor premiado pulsa
      x.fillStyle = `rgba(255,255,255,${.10 + .08 * Math.sin(ts / 150)})`; x.fill();
    }

    x.save(); x.rotate(a0);                                     // raio dourado
    x.beginPath(); x.moveTo(segIn * .5, 0); x.lineTo(segOut, 0);
    x.strokeStyle = 'rgba(255,228,158,.52)'; x.lineWidth = Math.max(1, R * .0045); x.stroke();
    x.restore();

    /* ícone colado ao aro, sempre na vertical */
    x.save();
    x.translate(Math.cos(mid) * R * .70, Math.sin(mid) * R * .70);
    x.rotate(-wheelAngle);
    x.font = `${Math.round(R * .076)}px serif`; x.textAlign = 'center'; x.textBaseline = 'middle';
    x.fillText(prize.emoji, 0, 0);
    x.restore();

    /* nome para dentro, nunca de cabeça e nunca por cima do ícone */
    const onLeft = Math.cos(wheelAngle + mid) < 0;
    const txtOut = R * .60, maxW = txtOut - segIn - R * .03;
    x.save(); x.rotate(mid); x.textBaseline = 'middle';
    x.fillStyle = inkFor(col);
    x.font = `700 ${Math.round(R * .054)}px Oxanium, ui-monospace, monospace`;
    if (onLeft) { x.rotate(Math.PI); x.textAlign = 'left';  x.fillText(prize.name, -txtOut, 0, maxW); }
    else        {                    x.textAlign = 'right'; x.fillText(prize.name,  txtOut, 0, maxW); }
    x.restore();
  }
  x.restore();

  /* medalhão central: monograma RF e quatro gemas da casa */
  const hubR = R * .15;
  const hg = x.createLinearGradient(-hubR, -hubR, hubR, hubR);
  hg.addColorStop(0, '#FFFBEB'); hg.addColorStop(.36, '#E6BE64'); hg.addColorStop(.62, '#9C6B18'); hg.addColorStop(1, '#FAE7B0');
  x.beginPath(); x.arc(0, 0, hubR, 0, TAU); x.fillStyle = hg; x.fill();
  x.lineWidth = Math.max(1, R * .009); x.strokeStyle = '#2E1F08'; x.stroke();
  x.beginPath(); x.arc(0, 0, hubR * .78, 0, TAU);
  x.lineWidth = Math.max(1, R * .0035); x.strokeStyle = 'rgba(46,31,8,.45)'; x.stroke();

  const gems = ['#FF6A00', '#FFC93C', '#00D66B', '#F4F1E7'];
  for (let g = 0; g < 4; g++) {
    const a = g * Math.PI / 2 - Math.PI / 4 + wheelAngle;
    x.beginPath(); x.arc(Math.cos(a) * hubR * .88, Math.sin(a) * hubR * .88, R * .011, 0, TAU);
    x.fillStyle = gems[g] as string; x.fill();
    x.lineWidth = Math.max(1, R * .003); x.strokeStyle = 'rgba(46,31,8,.7)'; x.stroke();
  }

  x.font = `${Math.round(hubR * .95)}px "Alfa Slab One", Rockwell, Georgia, serif`;
  x.textAlign = 'center'; x.textBaseline = 'middle';
  x.fillStyle = 'rgba(255,248,222,.5)'; x.fillText('RF', 0, hubR * .045);
  x.fillStyle = '#3A2708';              x.fillText('RF', 0, hubR * .015);

  /* brilho de vidro na metade superior */
  const gloss = x.createRadialGradient(-R * .3, -R * .45, 0, -R * .3, -R * .45, R * 1.05);
  gloss.addColorStop(0, 'rgba(255,255,255,.16)');
  gloss.addColorStop(.42, 'rgba(255,255,255,.04)');
  gloss.addColorStop(1, 'rgba(255,255,255,0)');
  x.beginPath(); x.arc(0, 0, rimOut, 0, TAU); x.fillStyle = gloss; x.fill();

  /* ponteiro */
  x.save();
  x.translate(0, -R * .90);
  x.shadowColor = 'rgba(0,0,0,.85)'; x.shadowBlur = R * .06; x.shadowOffsetY = R * .014;
  const pw = R * .075, ph = R * .175;
  x.beginPath();
  x.moveTo(0, ph); x.lineTo(-pw, -ph * .26); x.lineTo(-pw * .48, -ph * .62);
  x.lineTo(pw * .48, -ph * .62); x.lineTo(pw, -ph * .26); x.closePath();
  const pg = x.createLinearGradient(-pw, -ph, pw, ph);
  pg.addColorStop(0, '#FFFCEF'); pg.addColorStop(.4, '#FFD269'); pg.addColorStop(1, '#96650F');
  x.fillStyle = pg; x.fill();
  x.shadowColor = 'transparent';
  x.lineWidth = Math.max(1, R * .006); x.strokeStyle = '#2E1F08'; x.stroke();
  x.beginPath(); x.arc(0, -ph * .72, pw * .52, 0, TAU);          // remate esférico
  x.fillStyle = pg; x.fill(); x.stroke();
  x.beginPath(); x.arc(-pw * .16, -ph * .85, pw * .16, 0, TAU);
  x.fillStyle = 'rgba(255,255,255,.6)'; x.fill();
  x.restore();
}

function wheelTick(ts: number): void {
  drawWheel(ts);
  wheelRAF = q('#act-wheel').classList.contains('on') ? requestAnimationFrame(wheelTick) : null;
}

export function startWheelLoop(): void {
  if (!wheelRAF) wheelRAF = requestAnimationFrame(wheelTick);
}

/** A roda escolhe qual prêmio entra em jogo; ao parar, chama o sorteio desse prêmio. */
export function spinWheel(): void {
  if (wheelSpinning) return;
  const ev = live.event || currentEvent();
  wheelPrizes = (ev ? eventPrizes(ev.id) : []).filter(p => p.status === 'pending').slice(0, 10);
  if (!wheelPrizes.length) { toast(t('msg.noprizes')); return; }

  live.phase = 'wheel'; act('wheel'); updateConsole();
  Snd.resume(); Snd.whoosh();
  wheelWin = -1; wheelSpinning = true; startWheelLoop();

  const TAU = Math.PI * 2, n = wheelPrizes.length;
  const target = Math.floor(Math.random() * n);
  const centre = (target + .5) / n * TAU;
  const start  = ((wheelAngle % TAU) + TAU) % TAU;
  wheelAngle = start;
  let delta = ((TAU - centre) % TAU) - start;
  while (delta < 0) delta += TAU;
  const finalAngle = start + TAU * 6 + delta;

  const t0 = performance.now(), dur = 5600;
  let lastSector = -1;

  (function frame(now: number) {
    const k = Math.min(1, (now - t0) / dur);
    const e = 1 - Math.pow(1 - k, 4.2);
    wheelAngle = start + (finalAngle - start) * e;
    const sector = Math.floor((((wheelAngle % TAU) + TAU) % TAU) / TAU * n);
    if (sector !== lastSector) { lastSector = sector; Snd.tick(); }
    if (k < 1) requestAnimationFrame(frame);
    else settle();
  })(performance.now());

  /* assentamento: o ponteiro "bate" um pouco antes de firmar */
  function settle(): void {
    const s0 = performance.now(), sd = 620;
    (function osc(now: number) {
      const k = Math.min(1, (now - s0) / sd);
      wheelAngle = finalAngle + Math.sin(k * Math.PI * 3) * .016 * (1 - k);
      if (k < 1) requestAnimationFrame(osc);
      else {
        wheelAngle = finalAngle; wheelSpinning = false; wheelWin = target;
        Snd.ding();
        setTimeout(() => { void startDraw(wheelPrizes[target] as Prize); }, 1100);
      }
    })(performance.now());
  }
}
